import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import GroupedMeterTile from './GroupedMeterTile';
import MeterFactorChart from './MeterFactorChart';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

interface MeterHealthModalProps {
  open: boolean;
  onClose: () => void;
  meterProductId: string;
}

interface MeterData {
  meterProducts: any[];
  inboundMeters: string[];
  outboundMeters: string[];
}

export const MeterHealthModal: React.FC<MeterHealthModalProps> = ({
  open,
  onClose,
  meterProductId,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [meterData, setMeterData] = useState<any>(null);
  const [meterId, setMeterId] = useState<string>('');
  const [productType, setProductType] = useState<string>('');
  const [viewMode, setViewMode] = useState<'numbers' | 'chart'>('numbers');
  const [showDetailedChart, setShowDetailedChart] = useState(false);

  useEffect(() => {
    if (open && meterProductId) {
      // Parse meter_product_id to extract meter and product
      // Format is like "BAY_B_ARM_1_REG" where last part is product
      const parts = meterProductId.split('_');
      const product = parts[parts.length - 1];
      const meterParts = parts.slice(0, -1);

      // The meter_id in the dashboard uses spaces, like "BAY B ARM 1"
      const meter = meterParts.join(' ');

      setMeterId(meter);
      setProductType(product);

      fetchMeterData(meterProductId);
    } else if (!open) {
      // Reset state when modal closes
      setShowDetailedChart(false);
      setMeterData(null);
      setLoading(true);
    }
  }, [open, meterProductId]);

  const fetchMeterData = async (meterProdId: string) => {
    setLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Fetch dashboard data to get meter information
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/dashboard`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      // Handle both direct response and wrapped response from API Gateway
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      if (data && (data.meterProducts || data.meter_products)) {
        // Find the specific meter-product combination
        const meterProducts = data.meterProducts || data.meter_products || [];
        const meterProduct = meterProducts.find((mp: any) =>
          mp.meter_product_id === meterProdId
        );

        if (meterProduct) {
          // Parse meter_id / product / flow-rank label from meter_product_id.
          // Multi-flow ids carry a HIGH/LOW/MID suffix (e.g. "BAY 25 POS 2_MPU10E10_HIGH").
          const rawParts = meterProdId.split('_');
          const lastPart = (rawParts[rawParts.length - 1] || '').toUpperCase();
          let flowLabel: string | undefined;
          let productParts = rawParts;
          if (lastPart === 'HIGH' || lastPart === 'LOW' || lastPart.startsWith('MID')) {
            flowLabel = lastPart;
            productParts = rawParts.slice(0, -1);
          }
          const product = productParts[productParts.length - 1];
          const meter = productParts.slice(0, -1).join(' ');

          // Transform data to match GroupedMeterTile format
          const productData = {
            productType: product,
            meterProductId: meterProdId,
            flowLabel,
            health: meterProduct.health_status || meterProduct.current_health || 'unknown',
            currentFactor: meterProduct.current_meter_factor || 1.0000,
            previousFactor: meterProduct.previous_meter_factor || 1.0000,
            controlLimits: meterProduct.control_limits || {
              ucl: meterProduct.ucl || 1.0025,
              lcl: meterProduct.lcl || 0.9975,
              center: meterProduct.baseline || 1.0000
            },
            statistics: meterProduct.statistics || {
              documentCount: meterProduct.document_count || 0,
              avgMeterFactor: meterProduct.average_meter_factor || meterProduct.avg_meter_factor || 1.0000,
              trend: meterProduct.trend || 'unknown',
              anomalyCount: meterProduct.anomaly_count || 0,
              daysSinceProving: meterProduct.days_since_proving || 0
            },
            lastProvingDate: meterProduct.last_proving_date
          };

          // Check meter direction from inbound/outbound arrays
          const inboundMeters = data.inboundMeters || data.inbound_meters || [];
          const direction = inboundMeters.includes(meter) ? 'inbound' : 'outbound';

          setMeterData({
            products: [productData],
            direction: direction
          });
        }
      }
    } catch (error) {
      console.error('Error fetching meter data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '400px',  // Match card width
          maxWidth: '90vw',
          '& .MuiDialogContent-root': {
            padding: 0
          }
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Meter Health: {meterId} - {productType}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : meterData ? (
          <GroupedMeterTile
            meterId={meterId}
            products={meterData.products}
            meterDirection={meterData.direction}
            onViewChart={(mId, productType) => {
              setShowDetailedChart(true);
            }}
            onMeterChanged={() => fetchMeterData(meterProductId)}   // maintenance change -> refetch, no page reload
            onViewModeChange={(mId, mode) => {
              setViewMode(mode);
            }}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">
              No data available for this meter
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>

    {/* Detailed Chart Modal - renders as a separate dialog on top */}
    <Dialog
      open={showDetailedChart}
      onClose={() => setShowDetailedChart(false)}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: '1400px',
          height: 'auto',
          maxHeight: '90vh',
          zIndex: (theme) => theme.zIndex.modal + 100  // Ensure it renders on top of MeterHealthModal
        }
      }}
    >
      <MeterFactorChart
        meterProductId={meterProductId}
        meterName={meterId}
        productType={productType}
        onClose={() => setShowDetailedChart(false)}
      />
    </Dialog>
    </>
  );
};

export default MeterHealthModal;